/**
 * @module engine3d/mesh
 * @summary Triangle-mesh container, the per-triangle edge data (`feat`), and
 *   the parametric primitives every 3D equipment model is assembled from.
 * @description
 *   A `Mesh` is a flat, allocation-friendly triangle soup: `pos` and `nrm` hold
 *   three floats per vertex, `idx` three vertex indices per triangle, and the
 *   optional `feat` one packed number per TRIANGLE describing its edges.
 *
 *   FACET NORMALS (KIT v2, decision D1). The renderer shades a triangle by the
 *   mean of its three vertex normals. v1 stored the SMOOTH surface normal on
 *   ring vertices shared between neighbouring quads, so the two halves of every
 *   quad averaged their corners 2:1 and 1:2 and came out different shades — the
 *   zig-zag on every silo, dome and motor. Every primitive now writes the
 *   facet's own normal on each of its vertices and never shares a vertex across
 *   facets, so both halves of a quad shade identically and a curved surface
 *   reads as clean, machined bands. Smoothness comes from segment count
 *   (`segForLod`), silhouettes from the dihedrals in `feat`.
 *
 *   FEATURE DATA (D2, D3). Each triangle's `feat` packs: which of its edges are
 *   FEATURE lines (creases, open boundaries, weld/course seams — always drawn),
 *   which are SMOOTH (silhouette candidates) with their signed dihedral, whether
 *   it and the next triangle form one planar quad (`F_PAIR`), and whether it is
 *   an overlay (`F_DECAL`). Primitives AUTHOR the feature/smooth intent where
 *   they emit the facet; `featurize` then resolves boundaries and dihedrals for
 *   the triangles the primitive just emitted, from the primitive's own geometry.
 *   Nothing is inferred later, in the scene or the renderer.
 *
 *   Modelling conventions used by every builder here and in `models/*.ts`:
 *     • Local origin is centred in X and Z, and sits on the FLOOR (y = 0).
 *     • "Height" always means the +Y extent.
 *
 *   // INVARIANT: a vertex is never shared between facets whose normals differ;
 *   //   every vertex of a facet carries that facet's normal.
 *   // INVARIANT: every emitted triangle winds counter-clockwise seen from the
 *   //   side its normal points to. `feat`'s dihedral sign and the renderer's
 *   //   in-plane "outward" vector (e_k × n) both depend on it.
 *   // INVARIANT: `feat`, when present, is read as one entry per triangle; a
 *   //   missing or short array means 0 ("no edge data") for the rest. `merge`
 *   //   and the scene copy honour that, so hand-built meshes stay safe.
 *   // HAZARD: a negative `scale` flips winding (and so the meaning of every
 *   //   stored dihedral). Mirror with a rotation instead.
 *   // HAZARD: `feat` is triangle-ordered. Anything that reorders or drops
 *   //   triangles must carry `feat` with it and clear a dangling F_PAIR.
 */
import { TAU, v3, vAdd, vCross, vDot, vMul, vNorm, vSub, type Vec2, type Vec3 } from './math3';

export interface Mesh {
  /** Vertex positions, 3 floats per vertex. */
  pos: number[];
  /** Vertex normals, 3 floats per vertex (unit length; the FACET normal). */
  nrm: number[];
  /** Triangle vertex indices, 3 per triangle. */
  idx: number[];
  /** One packed uint32 per TRIANGLE (edge flags + dihedrals). Absent = no edge data. */
  feat?: number[];
}

// ─── Per-triangle edge data ─────────────────────────────────────────────────
//
// Bits 0-2 FEATURE edge k (v_k → v_(k+1)%3), bits 3-5 SMOOTH edge k, bit 6
// PAIR (this triangle and the next are one planar quad; only the first carries
// it and the shared diagonal is never flagged), bit 7 DECAL, then the three
// edge dihedrals as int8 in bytes 1-3: radians = q · (π/2) / 127, positive when
// the edge is convex. The neighbour's normal is n·cos δ + s·sin δ with
// s = normalize(e_k × n).

export const F_E0 = 1;
export const F_E1 = 2;
export const F_E2 = 4;
export const F_S0 = 8;
export const F_S1 = 16;
export const F_S2 = 32;
export const F_PAIR = 64;
export const F_DECAL = 128;
const F_EDGES = 63;
const DIH_Q = Math.PI / 2 / 127;

const qDih = (a: number): number => Math.max(-127, Math.min(127, Math.round(a / DIH_Q)));

/** The six edge bits (feature + smooth) of a packed `feat` value. */
export const featEdges = (f: number): number => f & F_EDGES;

/** The signed dihedral of edge k, in radians (0 = none recorded). */
export function featDih(f: number, k: 0 | 1 | 2): number {
  let q = (f >>> (8 + 8 * k)) & 255;
  if (q > 127) q -= 256;
  return q * DIH_Q;
}

/** Pack edge bits, three dihedrals and the PAIR / DECAL flags into one value. */
export function packFeat(edges: number, dih: [number, number, number], pair: boolean, decal: boolean): number {
  let f = (edges & F_EDGES) | (pair ? F_PAIR : 0) | (decal ? F_DECAL : 0);
  for (let k = 0; k < 3; k++) f |= (qDih(dih[k]) & 255) << (8 + 8 * k);
  return f >>> 0;
}

function withDih(f: number, k: number, a: number): number {
  const sh = 8 + 8 * k;
  return ((f & ~(255 << sh)) | ((qDih(a) & 255) << sh)) >>> 0;
}

/** A profile point for `revolve`; `e` draws a feature line round this row. */
export interface ProfilePt extends Vec2 {
  e?: boolean;
}

export const emptyMesh = (): Mesh => ({ pos: [], nrm: [], idx: [] });

/** Append a vertex, returning its index. */
function push(m: Mesh, p: Vec3, n: Vec3): number {
  const i = m.pos.length / 3;
  m.pos.push(p.x, p.y, p.z);
  m.nrm.push(n.x, n.y, n.z);
  return i;
}

/** `m.feat`, padded with zeros up to the current triangle count. */
function featOf(m: Mesh): number[] {
  const tris = m.idx.length / 3;
  if (!m.feat) m.feat = [];
  while (m.feat.length < tris) m.feat.push(0);
  return m.feat;
}

/** Concatenate meshes into one (indices rebased, `feat` carried in order). */
export function merge(...meshes: Mesh[]): Mesh {
  const out: Mesh = { pos: [], nrm: [], idx: [], feat: [] };
  const f = out.feat!;
  for (const m of meshes) {
    const base = out.pos.length / 3;
    for (let i = 0; i < m.pos.length; i++) out.pos.push(m.pos[i]);
    for (let i = 0; i < m.nrm.length; i++) out.nrm.push(m.nrm[i]);
    for (let i = 0; i < m.idx.length; i++) out.idx.push(m.idx[i] + base);
    // INVARIANT: one entry per triangle, even for a hand-built mesh whose
    // `feat` is missing or short — otherwise every later triangle's edges and
    // pairs would belong to its neighbour.
    const tris = m.idx.length / 3;
    for (let t = 0; t < tris; t++) f.push(m.feat?.[t] ?? 0);
  }
  return out;
}

export function triCount(m: Mesh): number {
  return m.idx.length / 3;
}

/** Clear every feature/smooth edge bit: dense detail that must not draw lines. */
export function soft(m: Mesh): Mesh {
  const f = featOf(m);
  for (let t = 0; t < f.length; t++) f[t] = (f[t] & ~F_EDGES) >>> 0;
  return m;
}

/** Flag every triangle as an overlay (plates, screens, grilles, stripes). */
export function decal(m: Mesh): Mesh {
  const f = featOf(m);
  for (let t = 0; t < f.length; t++) f[t] = (f[t] | F_DECAL) >>> 0;
  return m;
}

/** Promote smooth edges to feature edges — small hard parts that should read as outlined. */
export function lined(m: Mesh): Mesh {
  const f = featOf(m);
  for (let t = 0; t < f.length; t++) {
    const s = (f[t] >> 3) & 7;
    f[t] = ((f[t] & ~(7 << 3)) | s) >>> 0;
  }
  return m;
}

// ─── Transforms (in place, returning the same mesh for chaining) ──────────
// `feat` is untouched: dihedrals are rotation-invariant, and a uniform or
// mildly non-uniform positive scale keeps them close enough to draw with.

export function translate(m: Mesh, x: number, y: number, z: number): Mesh {
  for (let i = 0; i < m.pos.length; i += 3) {
    m.pos[i] += x;
    m.pos[i + 1] += y;
    m.pos[i + 2] += z;
  }
  return m;
}

export function scale(m: Mesh, sx: number, sy: number, sz: number): Mesh {
  for (let i = 0; i < m.pos.length; i += 3) {
    m.pos[i] *= sx;
    m.pos[i + 1] *= sy;
    m.pos[i + 2] *= sz;
  }
  // Normals transform by the inverse-transpose; for a diagonal scale that is
  // simply the reciprocal per axis. Skipping this makes non-uniform scaled
  // cylinders light as if they were still round. Identical facet normals stay
  // identical, so the facet invariant survives.
  const ix = 1 / sx;
  const iy = 1 / sy;
  const iz = 1 / sz;
  for (let i = 0; i < m.nrm.length; i += 3) {
    const nx = m.nrm[i] * ix;
    const ny = m.nrm[i + 1] * iy;
    const nz = m.nrm[i + 2] * iz;
    const l = Math.hypot(nx, ny, nz) || 1;
    m.nrm[i] = nx / l;
    m.nrm[i + 1] = ny / l;
    m.nrm[i + 2] = nz / l;
  }
  return m;
}

function rotate(m: Mesh, a: number, ax: 0 | 1 | 2): Mesh {
  const c = Math.cos(a);
  const s = Math.sin(a);
  const apply = (arr: number[]) => {
    for (let i = 0; i < arr.length; i += 3) {
      const x = arr[i];
      const y = arr[i + 1];
      const z = arr[i + 2];
      if (ax === 0) {
        arr[i + 1] = y * c - z * s;
        arr[i + 2] = y * s + z * c;
      } else if (ax === 1) {
        arr[i] = x * c + z * s;
        arr[i + 2] = -x * s + z * c;
      } else {
        arr[i] = x * c - y * s;
        arr[i + 1] = x * s + y * c;
      }
    }
  };
  apply(m.pos);
  apply(m.nrm);
  return m;
}

export const rotateX = (m: Mesh, a: number): Mesh => rotate(m, a, 0);
export const rotateY = (m: Mesh, a: number): Mesh => rotate(m, a, 1);
export const rotateZ = (m: Mesh, a: number): Mesh => rotate(m, a, 2);

/** Axis-aligned bounds of a mesh (used for shadows, fitting and picking). */
export function bounds(m: Mesh): { min: Vec3; max: Vec3 } {
  if (!m.pos.length) return { min: v3(), max: v3() };
  let x0 = Infinity;
  let y0 = Infinity;
  let z0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  let z1 = -Infinity;
  for (let i = 0; i < m.pos.length; i += 3) {
    if (m.pos[i] < x0) x0 = m.pos[i];
    if (m.pos[i] > x1) x1 = m.pos[i];
    if (m.pos[i + 1] < y0) y0 = m.pos[i + 1];
    if (m.pos[i + 1] > y1) y1 = m.pos[i + 1];
    if (m.pos[i + 2] < z0) z0 = m.pos[i + 2];
    if (m.pos[i + 2] > z1) z1 = m.pos[i + 2];
  }
  return { min: v3(x0, y0, z0), max: v3(x1, y1, z1) };
}

// ─── Segment policy (§2.1) ────────────────────────────────────────────────

/** How much a round surface matters: sets its segment count. */
export type Role = 'hero' | 'body' | 'detail' | 'fine';

const SEG_RULE: Record<Exclude<Role, 'fine'>, [number, number, number]> = {
  hero: [7, 24, 48],
  body: [5, 16, 32],
  detail: [4, 8, 16],
};

/**
 * Segments round a circle of radius `r` (world units) for a role, at a detail
 * level. Always a multiple of 4, so E/W/N/S ports land on a vertex and nested
 * parts that share an axis line up facet to facet. `kit.segFor` is the
 * builders' entry point; this pure form exists so the rule has one home.
 */
export function segForLod(r: number, role: Role, lod: 0 | 1 = 0): number {
  let seg: number;
  if (role === 'fine') seg = 8;
  else {
    const [k, lo, hi] = SEG_RULE[role];
    seg = Math.max(lo, Math.min(hi, 4 * Math.round((k * Math.sqrt(Math.max(r, 0.01))) / 4)));
  }
  if (lod === 1) seg = Math.max(8, 4 * Math.round(seg / 8));
  return seg;
}

// ─── Low-level emitters ───────────────────────────────────────────────────

/** Newell normal of a polygon (unnormalised; its length is twice the area). */
function newell(pts: Vec3[]): Vec3 {
  let x = 0;
  let y = 0;
  let z = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    x += (a.y - b.y) * (a.z + b.z);
    y += (a.z - b.z) * (a.x + b.x);
    z += (a.x - b.x) * (a.y + b.y);
  }
  return v3(x, y, z);
}

const dist = (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

/** Reverse the 4-bit quad edge mask (ab,bc,cd,da) for the (a,d,c,b) order. */
const flip4 = (m: number): number => ((m & 1) << 3) | ((m & 2) << 1) | ((m & 4) >> 1) | ((m & 8) >> 3);
/** Remap the 3-bit triangle edge mask (ab,bc,ca) for the (a,c,b) order. */
const flip3 = (m: number): number => ((m & 1) << 2) | (m & 2) | ((m & 4) >> 2);

interface EmitOpts {
  /** Stored normal. Default: the facet's own geometric normal. */
  n?: Vec3;
  /** Orientation only: the facet is wound so its normal agrees with this. */
  hint?: Vec3;
  /** Authored FEATURE edges (quad: ab,bc,cd,da = 1,2,4,8; tri: ab,bc,ca = 1,2,4). */
  edges?: number;
  /** Authored SMOOTH edges (same bit layout): never promoted to a crease. */
  smooth?: number;
  /** Dihedrals to store up front (featurize overwrites those it can measure). */
  dih?: number[];
  /** Per-vertex values pushed in emission order (the flow animation's arc). */
  arcs?: number[];
  arcOut?: number[];
}

function emitTri(m: Mesh, a: Vec3, b: Vec3, c: Vec3, o: EmitOpts): boolean {
  const g = vCross(vSub(b, a), vSub(c, a));
  const gl = Math.hypot(g.x, g.y, g.z);
  if (gl < 1e-12) return false;
  const want = o.n ?? o.hint;
  let fe = o.edges ?? 0;
  let sm = o.smooth ?? 0;
  let dih = o.dih;
  let arcs = o.arcs;
  if (want && vDot(want, g) < 0) {
    const t = b;
    b = c;
    c = t;
    fe = flip3(fe);
    sm = flip3(sm);
    if (dih) dih = [dih[2], dih[1], dih[0]];
    if (arcs) arcs = [arcs[0], arcs[2], arcs[1]];
  }
  const n = o.n ? vNorm(o.n) : vMul(vDot(want ?? g, g) < 0 ? vMul(g, -1) : g, 1 / gl);
  const f = featOf(m);
  const i = push(m, a, n);
  push(m, b, n);
  push(m, c, n);
  m.idx.push(i, i + 1, i + 2);
  let v = (fe & 7) | ((sm & 7) << 3);
  if (dih) for (let k = 0; k < 3; k++) v = withDih(v, k, dih[k] ?? 0);
  f.push(v >>> 0);
  if (o.arcOut && arcs) o.arcOut.push(arcs[0], arcs[1], arcs[2]);
  return true;
}

function emitQuad(m: Mesh, a: Vec3, b: Vec3, c: Vec3, d: Vec3, o: EmitOpts): void {
  // A collapsed corner turns the quad into a triangle (apex rows, poles, the
  // pinched corner of a rounded box).
  const eps = 1e-7;
  const ab = dist(a, b) < eps;
  const bc = dist(b, c) < eps;
  const cd = dist(c, d) < eps;
  const da = dist(d, a) < eps;
  const fe = o.edges ?? 0;
  const sm = o.smooth ?? 0;
  const arcs = o.arcs;
  const sub = (x: Vec3, y: Vec3, z: Vec3, ex: number, sx: number, ar?: number[]) =>
    emitTri(m, x, y, z, { ...o, edges: ex, smooth: sx, dih: undefined, arcs: ar });
  // Map quad-edge bits to the triangle that keeps them.
  const bit = (mask: number, q: number) => ((mask >> q) & 1) === 1;
  const tri3 = (mask: number, e0: number, e1: number, e2: number) =>
    (bit(mask, e0) ? 1 : 0) | (bit(mask, e1) ? 2 : 0) | (bit(mask, e2) ? 4 : 0);
  if (ab || cd || bc || da) {
    if ((ab && cd) || (bc && da)) return;
    if (ab) sub(a, c, d, tri3(fe, 1, 2, 3), tri3(sm, 1, 2, 3), arcs && [arcs[0], arcs[2], arcs[3]]);
    else if (bc) sub(a, b, d, tri3(fe, 0, 2, 3), tri3(sm, 0, 2, 3), arcs && [arcs[0], arcs[1], arcs[3]]);
    else if (cd) sub(a, b, c, tri3(fe, 0, 1, 3), tri3(sm, 0, 1, 3), arcs && [arcs[0], arcs[1], arcs[2]]);
    else sub(a, b, c, tri3(fe, 0, 1, 2), tri3(sm, 0, 1, 2), arcs && [arcs[0], arcs[1], arcs[2]]);
    return;
  }
  let g = newell([a, b, c, d]);
  let gl = Math.hypot(g.x, g.y, g.z);
  if (gl < 1e-12) return;
  const want = o.n ?? o.hint;
  const A = a;
  let B = b;
  const C = c;
  let D = d;
  let FE = fe;
  let SM = sm;
  let AR = arcs;
  let dih = o.dih;
  if (want && vDot(want, g) < 0) {
    B = d;
    D = b;
    FE = flip4(fe);
    SM = flip4(sm);
    if (AR) AR = [AR[0], AR[3], AR[2], AR[1]];
    if (dih) dih = [dih[3], dih[2], dih[1], dih[0]];
    g = vMul(g, -1);
  }
  gl = Math.hypot(g.x, g.y, g.z);
  const gn = vMul(g, 1 / gl);
  // Planarity: a twisted quad (a helicoid step, a clamped mitre) is emitted as
  // two honest triangles rather than a PAIR that is not flat.
  const size = Math.max(dist(A, C), dist(B, D));
  const ctr = vMul(vAdd(vAdd(A, B), vAdd(C, D)), 0.25);
  let off = 0;
  for (const q of [A, B, C, D]) off = Math.max(off, Math.abs(vDot(vSub(q, ctr), gn)));
  if (off > 1e-5 * size + 1e-9) {
    const t0 = (FE & 1) | (FE & 2);
    const s0 = (SM & 1) | (SM & 2);
    const t1 = ((FE >> 2) & 1) << 1 | ((FE >> 3) & 1) << 2;
    const s1 = ((SM >> 2) & 1) << 1 | ((SM >> 3) & 1) << 2;
    emitTri(m, A, B, C, { hint: gn, edges: t0, smooth: s0, arcs: AR && [AR[0], AR[1], AR[2]], arcOut: o.arcOut });
    emitTri(m, A, C, D, { hint: gn, edges: t1, smooth: s1, arcs: AR && [AR[0], AR[2], AR[3]], arcOut: o.arcOut });
    return;
  }
  const n = o.n ? vNorm(o.n) : gn;
  const f = featOf(m);
  const i = push(m, A, n);
  push(m, B, n);
  push(m, C, n);
  push(m, D, n);
  m.idx.push(i, i + 1, i + 2, i, i + 2, i + 3);
  let t0 = F_PAIR;
  let t1 = 0;
  if (FE & 1) t0 |= F_E0;
  if (FE & 2) t0 |= F_E1;
  if (FE & 4) t1 |= F_E1;
  if (FE & 8) t1 |= F_E2;
  if (SM & 1) t0 |= F_S0;
  if (SM & 2) t0 |= F_S1;
  if (SM & 4) t1 |= F_S1;
  if (SM & 8) t1 |= F_S2;
  if (dih) {
    t0 = withDih(withDih(t0, 0, dih[0] ?? 0), 1, dih[1] ?? 0);
    t1 = withDih(withDih(t1, 1, dih[2] ?? 0), 2, dih[3] ?? 0);
  }
  f.push(t0 >>> 0, t1 >>> 0);
  if (o.arcOut && AR) o.arcOut.push(AR[0], AR[1], AR[2], AR[3]);
}

/**
 * Emit a planar quad a-b-c-d as (a,b,c)+(a,c,d) with F_PAIR set and one facet
 * normal (default: its own geometric normal). If `n` is given and disagrees
 * with the winding, the quad is re-wound to match — so a caller can never
 * produce an inside-out facet. A non-planar quad is split into two triangles
 * with their own normals and no PAIR. `edges` authors feature lines
 * (ab, bc, cd, da = 1, 2, 4, 8). Call `featurize` once the part is complete.
 */
export function quad(
  m: Mesh,
  a: Vec3,
  b: Vec3,
  c: Vec3,
  d: Vec3,
  o: { n?: Vec3; edges?: number; smooth?: number; dih?: [number, number, number, number]; hint?: Vec3 } = {},
): void {
  emitQuad(m, a, b, c, d, o);
}

/** Emit one triangle (edges ab, bc, ca = 1, 2, 4), wound to agree with `n` if given. */
export function tri(
  m: Mesh,
  a: Vec3,
  b: Vec3,
  c: Vec3,
  o: { n?: Vec3; edges?: number; smooth?: number; dih?: [number, number, number]; hint?: Vec3 } = {},
): void {
  emitTri(m, a, b, c, o);
}

/**
 * Resolve the edge data of the triangles emitted since `from`: an edge with no
 * matching half-edge is an open boundary (feature); an authored feature stays
 * a feature on BOTH sides; an authored smooth edge stays smooth; anything else
 * is a feature above `creaseDeg` and smooth above a fraction of a degree. Every
 * matched edge gets its measured signed dihedral. A PAIR's diagonal is never
 * flagged. Primitives call this on their own output; a builder that emits
 * custom geometry with `quad`/`tri` should call it once at the end.
 */
export function featurize(m: Mesh, o: { creaseDeg?: number; from?: number } = {}): Mesh {
  const f = featOf(m);
  const tris = m.idx.length / 3;
  const from = o.from ?? 0;
  const crease = ((o.creaseDeg ?? 35) * Math.PI) / 180;
  const tiny = 0.004;
  const P = m.pos;
  const N = m.nrm;
  const I = m.idx;
  // Quantised position ids, so duplicated facet vertices match up.
  const ids = new Map<string, number>();
  const vid: number[] = [];
  const idOf = (vi: number): number => {
    const cached = vid[vi];
    if (cached !== undefined) return cached;
    const k = `${Math.round(P[vi * 3] * 1e4)},${Math.round(P[vi * 3 + 1] * 1e4)},${Math.round(P[vi * 3 + 2] * 1e4)}`;
    let id = ids.get(k);
    if (id === undefined) {
      id = ids.size;
      ids.set(k, id);
    }
    vid[vi] = id;
    return id;
  };
  const isDiag = (t: number, k: number): boolean =>
    (k === 2 && (f[t] & F_PAIR) !== 0) || (k === 0 && t > from && (f[t - 1] & F_PAIR) !== 0);
  const BIG = 4194304; // 2^22 distinct positions per primitive is far beyond any part
  const half = new Map<number, number>();
  for (let t = from; t < tris; t++)
    for (let k = 0; k < 3; k++) {
      if (isDiag(t, k)) continue;
      const a = idOf(I[t * 3 + k]);
      const b = idOf(I[t * 3 + ((k + 1) % 3)]);
      if (a === b) continue;
      const key = a * BIG + b;
      if (!half.has(key)) half.set(key, t * 3 + k);
    }
  const next: number[] = f.slice();
  for (let t = from; t < tris; t++) {
    let v = next[t];
    for (let k = 0; k < 3; k++) {
      if (isDiag(t, k)) {
        v &= ~((1 << k) | (8 << k));
        continue;
      }
      const via = I[t * 3 + k];
      const vib = I[t * 3 + ((k + 1) % 3)];
      const a = idOf(via);
      const b = idOf(vib);
      if (a === b) {
        v &= ~((1 << k) | (8 << k));
        continue;
      }
      const tw = half.get(b * BIG + a);
      const eB = 1 << k;
      const sB = 8 << k;
      if (tw === undefined) {
        v = (v | eB) & ~sB;
        continue;
      }
      const t2 = Math.floor(tw / 3);
      const k2 = tw % 3;
      const n0 = I[t * 3] * 3;
      const n2 = I[t2 * 3] * 3;
      const nA = v3(N[n0], N[n0 + 1], N[n0 + 2]);
      const nB = v3(N[n2], N[n2 + 1], N[n2 + 2]);
      const e = v3(P[vib * 3] - P[via * 3], P[vib * 3 + 1] - P[via * 3 + 1], P[vib * 3 + 2] - P[via * 3 + 2]);
      const s = vNorm(vCross(e, nA));
      const dih = Math.atan2(vDot(nB, s), vDot(nB, nA));
      const authE = (f[t] & eB) !== 0 || (f[t2] & (1 << k2)) !== 0;
      const authS = (f[t] & sB) !== 0 || (f[t2] & (8 << k2)) !== 0;
      if (authE || (!authS && Math.abs(dih) > crease)) v = (v | eB) & ~sB;
      else if (Math.abs(dih) > tiny) v = (v | sB) & ~eB;
      else v &= ~(eB | sB);
      v = withDih(v, k, dih);
    }
    next[t] = v >>> 0;
  }
  for (let t = from; t < tris; t++) f[t] = next[t];
  return m;
}

// ─── Primitives ──────────────────────────────────────────────────────────

/** A rectangular box, centred in X/Z, resting on y = 0. All 12 edges featured. */
export function box(w: number, h: number, d: number): Mesh {
  const m = emptyMesh();
  const hx = w / 2;
  const hz = d / 2;
  const faces: [Vec3, Vec3, Vec3, Vec3, Vec3][] = [
    [v3(-hx, 0, hz), v3(hx, 0, hz), v3(hx, h, hz), v3(-hx, h, hz), v3(0, 0, 1)],
    [v3(hx, 0, -hz), v3(-hx, 0, -hz), v3(-hx, h, -hz), v3(hx, h, -hz), v3(0, 0, -1)],
    [v3(hx, 0, hz), v3(hx, 0, -hz), v3(hx, h, -hz), v3(hx, h, hz), v3(1, 0, 0)],
    [v3(-hx, 0, -hz), v3(-hx, 0, hz), v3(-hx, h, hz), v3(-hx, h, -hz), v3(-1, 0, 0)],
    [v3(-hx, h, hz), v3(hx, h, hz), v3(hx, h, -hz), v3(-hx, h, -hz), v3(0, 1, 0)],
    [v3(-hx, 0, -hz), v3(hx, 0, -hz), v3(hx, 0, hz), v3(-hx, 0, hz), v3(0, -1, 0)],
  ];
  for (const [a, b, c, d2, n] of faces) emitQuad(m, a, b, c, d2, { n, edges: 15 });
  return featurize(m);
}

/** A box centred on the origin in all three axes. */
export function boxCentered(w: number, h: number, d: number): Mesh {
  return translate(box(w, h, d), 0, -h / 2, 0);
}

export interface FrustumOpts {
  seg?: number;
  capTop?: boolean;
  capBottom?: boolean;
  /** Sweep less than a full turn (radians) — used for half-shells and troughs. */
  arc?: number;
  arcStart?: number;
  /** A 45° chamfer of this size round the top rim (+2·seg triangles). */
  lipTop?: number;
  /** A 45° chamfer of this size round the bottom rim (+2·seg triangles). */
  lipBottom?: number;
}

/**
 * The shared surface-of-revolution emitter behind `frustum` and `revolve`.
 * Profile rows run bottom to top; the outward normal is the profile tangent
 * turned to its right (the v1 convention). Meridian edges are authored smooth,
 * rows flagged `e` (or turning more than `crease`) are authored features.
 */
function lathe(
  prof: ProfilePt[],
  seg: number,
  arc: number,
  a0: number,
  capBottom: boolean,
  capTop: boolean,
  creaseDeg: number,
): Mesh {
  const m = emptyMesh();
  seg = Math.max(3, Math.round(seg));
  const closed = Math.abs(arc - TAU) < 1e-6;
  const crease = (creaseDeg * Math.PI) / 180;
  // Drop consecutive duplicates (they would only emit degenerate bands).
  const pr: ProfilePt[] = [];
  for (const q of prof) {
    const last = pr[pr.length - 1];
    if (last && Math.abs(last.x - q.x) < 1e-7 && Math.abs(last.y - q.y) < 1e-7) {
      if (q.e) last.e = true;
      continue;
    }
    pr.push({ x: Math.max(0, q.x), y: q.y, e: q.e });
  }
  const ang = (i: number) => a0 + (arc * i) / seg;
  const at = (q: Vec2, i: number) => v3(Math.cos(ang(i)) * q.x, q.y, Math.sin(ang(i)) * q.x);
  const rowFeat: boolean[] = pr.map((q, k) => {
    if (q.e) return true;
    if (k === 0 || k === pr.length - 1) return false;
    const t0 = { x: q.x - pr[k - 1].x, y: q.y - pr[k - 1].y };
    const t1 = { x: pr[k + 1].x - q.x, y: pr[k + 1].y - q.y };
    const turn = Math.abs(Math.atan2(t0.x * t1.y - t0.y * t1.x, t0.x * t1.x + t0.y * t1.y));
    return turn > crease;
  });
  for (let k = 0; k < pr.length - 1; k++) {
    const q0 = pr[k];
    const q1 = pr[k + 1];
    const tr = q1.x - q0.x;
    const ty = q1.y - q0.y;
    const tl = Math.hypot(tr, ty) || 1;
    const nr = ty / tl;
    const ny = -tr / tl;
    if (q0.x < 1e-7 && q1.x < 1e-7) continue;
    for (let i = 0; i < seg; i++) {
      const tm = (ang(i) + ang(i + 1)) / 2;
      const hint = v3(Math.cos(tm) * nr, ny, Math.sin(tm) * nr);
      emitQuad(m, at(q0, i), at(q0, i + 1), at(q1, i + 1), at(q1, i), {
        hint,
        edges: (rowFeat[k] ? 1 : 0) | (rowFeat[k + 1] ? 4 : 0),
        smooth: 2 | 8,
      });
    }
  }
  const cap = (q: Vec2, up: boolean) => {
    if (q.x <= 1e-6) return;
    const n = v3(0, up ? 1 : -1, 0);
    const c = v3(0, q.y, 0);
    for (let i = 0; i < seg; i++) emitTri(m, c, at(q, i), at(q, i + 1), { n });
  };
  if (pr.length) {
    if (capBottom) cap(pr[0], false);
    if (capTop) cap(pr[pr.length - 1], true);
  }
  void closed;
  return featurize(m, { creaseDeg });
}

/**
 * A cone frustum about the +Y axis: radius `r0` at y = 0 → `r1` at y = h.
 * Set the radii equal for a cylinder, `r1 = 0` for a cone. This one primitive
 * covers silos, tanks, hoppers, cyclones, pipes, rollers and drums. Rims are
 * featured, the wall is smooth (silhouette only); `lipTop`/`lipBottom` add a
 * 45° chamfer that catches the key light as a bright ring.
 */
export function frustum(r0: number, r1: number, h: number, o: FrustumOpts = {}): Mesh {
  const seg = o.seg ?? 28;
  const arc = o.arc ?? TAU;
  const a0 = o.arcStart ?? 0;
  const rAt = (y: number) => r0 + ((r1 - r0) * y) / (h || 1);
  const lb = r0 > 1e-6 ? Math.min(o.lipBottom ?? 0, r0 * 0.9, h * 0.45) : 0;
  const lt = r1 > 1e-6 ? Math.min(o.lipTop ?? 0, r1 * 0.9, h * 0.45) : 0;
  const prof: ProfilePt[] = [];
  if (lb > 0) prof.push({ x: r0 - lb, y: 0, e: true }, { x: rAt(lb), y: lb, e: true });
  else prof.push({ x: r0, y: 0, e: true });
  if (lt > 0) prof.push({ x: rAt(h - lt), y: h - lt, e: true }, { x: r1 - lt, y: h, e: true });
  else prof.push({ x: r1, y: h, e: true });
  return lathe(prof, seg, arc, a0, o.capBottom ?? true, o.capTop ?? true, 89);
}

/** Convenience: a plain cylinder about +Y, base on y = 0. */
export const cylinder = (r: number, h: number, o: FrustumOpts = {}): Mesh => frustum(r, r, h, o);

/** Convenience: a cone about +Y, apex up. */
export const cone = (r: number, h: number, o: FrustumOpts = {}): Mesh => frustum(r, 0, h, o);

/** A cylinder lying along the X axis, centred on the origin. */
export function cylinderX(r: number, len: number, seg = 24): Mesh {
  const m = cylinder(r, len, { seg });
  rotateZ(m, Math.PI / 2);
  return translate(m, len / 2, 0, 0);
}

/** A cylinder lying along the Z axis, centred on the origin. */
export function cylinderZ(r: number, len: number, seg = 24): Mesh {
  const m = cylinder(r, len, { seg });
  rotateX(m, -Math.PI / 2);
  return translate(m, 0, 0, len / 2);
}

/** A UV sphere centred on the origin: facet quads, triangles at the poles. */
export function sphere(r: number, seg = 24, rings = 12): Mesh {
  const m = emptyMesh();
  const P = (j: number, i: number) => {
    const phi = (Math.PI * j) / rings;
    const th = (TAU * i) / seg;
    return v3(r * Math.sin(phi) * Math.cos(th), r * Math.cos(phi), r * Math.sin(phi) * Math.sin(th));
  };
  for (let j = 0; j < rings; j++)
    for (let i = 0; i < seg; i++) {
      const phi = (Math.PI * (j + 0.5)) / rings;
      const th = (TAU * (i + 0.5)) / seg;
      const hint = v3(Math.sin(phi) * Math.cos(th), Math.cos(phi), Math.sin(phi) * Math.sin(th));
      emitQuad(m, P(j, i), P(j + 1, i), P(j + 1, i + 1), P(j, i + 1), { hint, smooth: 15 });
    }
  return featurize(m);
}

/**
 * Surface of revolution about +Y from a profile of (radius, height) pairs.
 * The workhorse for pressure vessels: dished ends, knuckles, skirts and conical
 * bottoms are all just profile points. A row flagged `e` (a weld line, a course
 * seam) or turning more than `creaseDeg` is drawn as a feature line; the rest
 * of the surface is smooth.
 */
export function revolve(
  profile: (Vec2 | ProfilePt)[],
  seg = 28,
  capEnds = true,
  o: { arc?: number; arcStart?: number; creaseDeg?: number } = {},
): Mesh {
  return lathe(
    profile.map((q) => ({ x: q.x, y: q.y, e: (q as ProfilePt).e })),
    seg,
    o.arc ?? TAU,
    o.arcStart ?? 0,
    capEnds,
    capEnds,
    o.creaseDeg ?? 35,
  );
}

/** Signed area of a 2D polygon (positive = counter-clockwise). */
function area2(poly: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}

/**
 * Ear-clipping triangulation of any simple polygon (either winding). Returns
 * index triples in the polygon's own winding; collinear vertices are dropped
 * rather than emitted as slivers.
 */
export function triangulate(poly: Vec2[]): number[] {
  const n = poly.length;
  if (n < 3) return [];
  const sgn = area2(poly) >= 0 ? 1 : -1;
  const V: number[] = [];
  for (let i = 0; i < n; i++) V.push(i);
  const out: number[] = [];
  const cr = (a: Vec2, b: Vec2, c: Vec2) => ((b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)) * sgn;
  const inside = (p: Vec2, a: Vec2, b: Vec2, c: Vec2) =>
    cr(a, b, p) > 1e-12 && cr(b, c, p) > 1e-12 && cr(c, a, p) > 1e-12;
  let guard = 0;
  while (V.length > 3 && guard++ < n * n + 10) {
    let done = false;
    for (let i = 0; i < V.length; i++) {
      const ia = V[(i + V.length - 1) % V.length];
      const ib = V[i];
      const ic = V[(i + 1) % V.length];
      const c = cr(poly[ia], poly[ib], poly[ic]);
      const scale2 = Math.max(1e-12, Math.hypot(poly[ic].x - poly[ia].x, poly[ic].y - poly[ia].y) ** 2);
      if (Math.abs(c) < 1e-9 * scale2) {
        V.splice(i, 1);
        done = true;
        break;
      }
      if (c < 0) continue;
      let blocked = false;
      for (const q of V) {
        if (q === ia || q === ib || q === ic) continue;
        if (inside(poly[q], poly[ia], poly[ib], poly[ic])) {
          blocked = true;
          break;
        }
      }
      if (blocked) continue;
      out.push(ia, ib, ic);
      V.splice(i, 1);
      done = true;
      break;
    }
    if (!done) {
      // Self-touching or numerically awkward outline: fall back to a fan step.
      out.push(V[V.length - 1], V[0], V[1]);
      V.splice(0, 1);
    }
  }
  if (V.length === 3 && Math.abs(cr(poly[V[0]], poly[V[1]], poly[V[2]])) > 1e-12) out.push(V[0], V[1], V[2]);
  return out;
}

/**
 * Extrude a closed 2D polygon (in the XZ plane: x → X, y → Z) upward by `h`.
 * Either winding works. Caps are ear-clipped, so C, H, L and other concave
 * outlines cap correctly; wall edges are features where the outline turns more
 * than 35°, smooth otherwise.
 */
export function extrude(poly: Vec2[], h: number, o: { caps?: 'both' | 'top' | 'none' } = {}): Mesh {
  const m = emptyMesh();
  const n = poly.length;
  if (n < 3) return m;
  const sgn = area2(poly) >= 0 ? 1 : -1;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    const hint = v3((b.y - a.y) * sgn, 0, -(b.x - a.x) * sgn);
    emitQuad(m, v3(a.x, 0, a.y), v3(b.x, 0, b.y), v3(b.x, h, b.y), v3(a.x, h, a.y), { n: vNorm(hint) });
  }
  const caps = o.caps ?? 'both';
  if (caps !== 'none') {
    const T = triangulate(poly);
    for (let t = 0; t < T.length; t += 3) {
      const A = poly[T[t]];
      const B = poly[T[t + 1]];
      const C = poly[T[t + 2]];
      emitTri(m, v3(A.x, h, A.y), v3(B.x, h, B.y), v3(C.x, h, C.y), { n: v3(0, 1, 0) });
      if (caps === 'both') emitTri(m, v3(A.x, 0, A.y), v3(B.x, 0, B.y), v3(C.x, 0, C.y), { n: v3(0, -1, 0) });
    }
  }
  return featurize(m);
}

/**
 * The shared section sweep behind `tube`, `sweep` and `prism`: carries a
 * closed 2D section (x = local right, y = local up) along a poly-line.
 *
 * Corners use the MITRE plane between the two adjacent segment directions, so a
 * bend keeps a constant section instead of pinching (which is what a naive
 * per-segment chain does at every elbow). The section frame is carried along
 * the path by parallel transport, so a run that turns twice doesn't twist
 * between the turns.
 */
function sweepCore(
  path: Vec3[],
  sec: Vec2[],
  o: {
    closed?: boolean;
    caps?: boolean;
    smooth?: boolean;
    creaseDeg?: number;
    arcOut?: number[];
    centreCap?: boolean;
    up?: Vec3;
  },
): Mesh {
  const pts: Vec3[] = [];
  for (const p of path) {
    const last = pts[pts.length - 1];
    if (last && dist(p, last) < 1e-4) continue;
    pts.push(p);
  }
  const closed = !!o.closed && pts.length > 2;
  if (closed && dist(pts[0], pts[pts.length - 1]) < 1e-4) pts.pop();
  const m = emptyMesh();
  if (pts.length < 2 || sec.length < 3) return m;
  const N = pts.length;
  const nSeg = closed ? N : N - 1;
  const dirs: Vec3[] = [];
  for (let i = 0; i < nSeg; i++) dirs.push(vNorm(vSub(pts[(i + 1) % N], pts[i])));
  const arc: number[] = [0];
  for (let i = 1; i <= nSeg; i++) arc.push(arc[i - 1] + dist(pts[i % N], pts[i - 1]));
  const S = sec.length;
  const sgn = area2(sec) >= 0 ? 1 : -1;

  // Parallel-transported reference frame.
  const d0 = dirs[0];
  let up = o.up ?? (Math.abs(d0.y) > 0.9 ? v3(0, 0, 1) : v3(0, 1, 0));
  let side = vNorm(vCross(d0, up));
  up = vNorm(vCross(side, d0));
  const frames: { side: Vec3; up: Vec3 }[] = [];
  const rows: Vec3[][] = [];
  const rot = (v: Vec3, from: Vec3, to: Vec3): Vec3 => {
    const axis = vCross(from, to);
    const sinA = Math.hypot(axis.x, axis.y, axis.z);
    if (sinA < 1e-6) return v;
    const k = vNorm(axis);
    const cosA = Math.max(-1, Math.min(1, vDot(from, to)));
    const a = Math.atan2(sinA, cosA);
    return vAdd(vAdd(vMul(v, Math.cos(a)), vMul(vCross(k, v), Math.sin(a))), vMul(k, vDot(k, v) * (1 - Math.cos(a))));
  };
  for (let i = 0; i < N; i++) {
    const dOut = closed ? dirs[i % nSeg] : dirs[Math.min(nSeg - 1, i)];
    const dIn = closed ? dirs[(i - 1 + nSeg) % nSeg] : dirs[Math.max(0, i - 1)];
    if (i > 0) {
      side = vNorm(rot(side, dirs[i - 1], dOut));
      up = vNorm(rot(up, dirs[i - 1], dOut));
    }
    frames.push({ side, up });
    const bis = vNorm(vAdd(dIn, dOut));
    const denom = Math.max(0.35, vDot(bis, dOut));
    const row: Vec3[] = [];
    for (let k = 0; k < S; k++) {
      const c = vAdd(vMul(side, sec[k].x), vMul(up, sec[k].y));
      const slide = -vDot(c, bis) / denom;
      row.push(vAdd(pts[i], vAdd(c, vMul(dOut, slide))));
    }
    rows.push(row);
  }
  const edgeOut = (fr: { side: Vec3; up: Vec3 }, k: number) => {
    const a = sec[k];
    const b = sec[(k + 1) % S];
    return vAdd(vMul(fr.side, (b.y - a.y) * sgn), vMul(fr.up, -(b.x - a.x) * sgn));
  };
  for (let i = 0; i < nSeg; i++) {
    const A = rows[i];
    const B = rows[(i + 1) % N];
    for (let k = 0; k < S; k++) {
      const kk = (k + 1) % S;
      emitQuad(m, A[k], A[kk], B[kk], B[k], {
        hint: edgeOut(frames[i], k),
        smooth: o.smooth ? 15 : 0,
        arcs: o.arcOut ? [arc[i], arc[i], arc[i + 1], arc[i + 1]] : undefined,
        arcOut: o.arcOut,
      });
    }
  }
  if (o.caps && !closed) {
    const ends: [number, Vec3, number][] = [
      [0, vMul(dirs[0], -1), 0],
      [N - 1, dirs[nSeg - 1], arc[nSeg]],
    ];
    for (const [ri, n, at] of ends) {
      const row = rows[ri];
      const ar = o.arcOut ? [at, at, at] : undefined;
      if (o.centreCap) {
        const c = pts[ri];
        for (let k = 0; k < S; k++) emitTri(m, c, row[k], row[(k + 1) % S], { n, arcs: ar, arcOut: o.arcOut });
      } else {
        const T = triangulate(sec);
        for (let t = 0; t < T.length; t += 3)
          emitTri(m, row[T[t]], row[T[t + 1]], row[T[t + 2]], { n, arcs: ar, arcOut: o.arcOut });
      }
    }
  }
  return featurize(m, { creaseDeg: o.creaseDeg ?? 35 });
}

/** A circle of `seg` points of radius r, as a 2D section. */
function circleSec(r: number, seg: number): Vec2[] {
  const out: Vec2[] = [];
  for (let k = 0; k < seg; k++) {
    const t = (TAU * k) / seg;
    out.push({ x: Math.cos(t) * r, y: Math.sin(t) * r });
  }
  return out;
}

/**
 * Sweep a circular section along a 3D poly-line — the pipe builder. Facet
 * bands, smooth walls; the cap rims (or an open end) are feature lines.
 *
 * @param arcOut  Optional: receives each EMITTED vertex's distance along the
 *   path, in world units. The flow animation is a function of exactly that,
 *   and taking it from the sweep itself is both exact and free — the
 *   alternative, working it back out by projecting every triangle onto the
 *   centre-line, costs the triangle count times the path length on every
 *   rebuild. Facet vertices are duplicated, and each copy gets its own entry.
 */
export function tube(path: Vec3[], r: number, seg = 14, capEnds = true, arcOut?: number[]): Mesh {
  return sweepCore(path, circleSec(r, seg), { caps: capEnds, smooth: true, arcOut, centreCap: true, creaseDeg: 60 });
}

/**
 * Carry any closed 2D section along a poly-line (handrail tubes, belts, cage
 * hoops, cable trays, helical guards). Same mitre and parallel-transport logic
 * as `tube`; section corners sharper than `creaseDeg` are feature lines.
 */
export function sweep(
  path: Vec3[],
  section: Vec2[],
  o: { closed?: boolean; caps?: boolean; creaseDeg?: number; up?: Vec3 } = {},
): Mesh {
  return sweepCore(path, section, { closed: o.closed, caps: o.caps ?? !o.closed, creaseDeg: o.creaseDeg, up: o.up });
}

/**
 * Extrude a 2D section (x = local right, y = local up) from `a` to `b` — every
 * structural member, trough and hood section. Local right is (b−a) × up, with
 * `up` defaulting to world +Y (world +Z for a vertical member).
 */
export function prism(
  section: Vec2[],
  a: Vec3,
  b: Vec3,
  o: { up?: Vec3; caps?: boolean; creaseDeg?: number } = {},
): Mesh {
  return sweepCore([a, b], section, { caps: o.caps ?? true, creaseDeg: o.creaseDeg, up: o.up });
}

/**
 * Round the corners of a poly-line with (quadratic Bézier) fillets of radius
 * `r`. Feeding this into `tube` is what turns a Manhattan route into pipework
 * with real bend radii rather than square kinks. New code wanting true arcs
 * uses `filletArc`.
 */
export function filletPath(path: Vec3[], r: number, segPerBend = 5): Vec3[] {
  if (path.length < 3) return path.slice();
  const out: Vec3[] = [path[0]];
  for (let i = 1; i < path.length - 1; i++) {
    const p = path[i];
    const a = vSub(path[i - 1], p);
    const b = vSub(path[i + 1], p);
    const la = Math.hypot(a.x, a.y, a.z);
    const lb = Math.hypot(b.x, b.y, b.z);
    if (la < 1e-4 || lb < 1e-4) continue;
    const na = vMul(a, 1 / la);
    const nb = vMul(b, 1 / lb);
    const cosA = Math.max(-0.999, Math.min(0.999, na.x * nb.x + na.y * nb.y + na.z * nb.z));
    const ang = Math.acos(cosA);
    // Nearly straight — no bend to build.
    if (ang > Math.PI - 0.05) {
      out.push(p);
      continue;
    }
    const cut = Math.min(r / Math.tan(ang / 2), la * 0.48, lb * 0.48);
    const s = vAdd(p, vMul(na, cut));
    const e = vAdd(p, vMul(nb, cut));
    // Quadratic Bezier through the corner approximates the arc closely enough
    // at pipe scale and costs a fraction of a true circular sweep.
    for (let k = 0; k <= segPerBend; k++) {
      const t = k / segPerBend;
      const u = 1 - t;
      out.push({
        x: u * u * s.x + 2 * u * t * p.x + t * t * e.x,
        y: u * u * s.y + 2 * u * t * p.y + t * t * e.y,
        z: u * u * s.z + 2 * u * t * p.z + t * t * e.z,
      });
    }
  }
  out.push(path[path.length - 1]);
  return out;
}

/**
 * Round the corners of a poly-line with TRUE circular fillets of radius `r`
 * (`segPer90` points per quarter turn). For new code; `filletPath` stays for
 * pipes, whose flow arc lengths are pinned by the flow suite.
 */
export function filletArc(path: Vec3[], r: number, segPer90 = 6): Vec3[] {
  if (path.length < 3) return path.slice();
  const out: Vec3[] = [path[0]];
  for (let i = 1; i < path.length - 1; i++) {
    const p = path[i];
    const a = vSub(path[i - 1], p);
    const b = vSub(path[i + 1], p);
    const la = Math.hypot(a.x, a.y, a.z);
    const lb = Math.hypot(b.x, b.y, b.z);
    if (la < 1e-4 || lb < 1e-4) continue;
    const na = vMul(a, 1 / la);
    const nb = vMul(b, 1 / lb);
    const ang = Math.acos(Math.max(-1, Math.min(1, vDot(na, nb))));
    if (ang > Math.PI - 0.02 || ang < 1e-3) {
      out.push(p);
      continue;
    }
    const cut = Math.min(r / Math.tan(ang / 2), la * 0.48, lb * 0.48);
    const rr = cut * Math.tan(ang / 2);
    const s = vAdd(p, vMul(na, cut));
    const e = vAdd(p, vMul(nb, cut));
    const c = vAdd(p, vMul(vNorm(vAdd(na, nb)), rr / Math.sin(ang / 2)));
    const u = vNorm(vSub(s, c));
    const w = vSub(e, c);
    const vp = vNorm(vSub(w, vMul(u, vDot(u, w))));
    const sweepA = Math.PI - ang;
    const steps = Math.max(1, Math.ceil((segPer90 * sweepA) / (Math.PI / 2)));
    for (let k = 0; k <= steps; k++) {
      const t = (sweepA * k) / steps;
      out.push(vAdd(c, vAdd(vMul(u, rr * Math.cos(t)), vMul(vp, rr * Math.sin(t)))));
    }
  }
  out.push(path[path.length - 1]);
  return out;
}

/**
 * Split a poly-line's long segments so no piece is longer than `step`.
 *
 * The shape is unchanged — every inserted point lies on the line it splits.
 * What changes is how finely a sweep along it is TESSELLATED, and that is the
 * whole reason this exists: a shaded triangle is one flat colour, so a straight
 * leg swept as a single ring-to-ring quad strip can only ever be one colour
 * down its whole length. Nothing can be shown travelling along a pipe that has
 * no sections between its ends.
 *
 * Expensive enough to be worth asking for rather than assuming: it is applied
 * only while the flow is running, and only to the runs that carry something.
 */
export function densify(path: Vec3[], step: number): Vec3[] {
  if (!(step > 0) || path.length < 2) return path.slice();
  const out: Vec3[] = [path[0]];
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    const d = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
    const n = Math.ceil(d / step);
    for (let k = 1; k < n; k++) {
      const t = k / n;
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t });
    }
    out.push(b);
  }
  return out;
}

/** A regular n-gon in the XZ plane, for footprints and extrusions. */
export function ngon(r: number, n: number, phase = 0): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const t = phase + (TAU * i) / n;
    out.push({ x: Math.cos(t) * r, y: Math.sin(t) * r });
  }
  return out;
}

/** A rectangle in the XZ plane, centred on the origin. */
export function rectPoly(w: number, d: number): Vec2[] {
  return [
    { x: -w / 2, y: -d / 2 },
    { x: w / 2, y: -d / 2 },
    { x: w / 2, y: d / 2 },
    { x: -w / 2, y: d / 2 },
  ];
}

/** A torus arc about the +Y axis — the bend body of a fabricated elbow. Ends featured. */
export function torusArc(R: number, r: number, a0: number, a1: number, segMajor = 12, segMinor = 12): Mesh {
  const m = emptyMesh();
  const P = (i: number, k: number) => {
    const t = a0 + ((a1 - a0) * i) / segMajor;
    const u = (TAU * k) / segMinor;
    return v3(Math.cos(t) * (R + r * Math.cos(u)), r * Math.sin(u), Math.sin(t) * (R + r * Math.cos(u)));
  };
  for (let i = 0; i < segMajor; i++)
    for (let k = 0; k < segMinor; k++) {
      const t = a0 + ((a1 - a0) * (i + 0.5)) / segMajor;
      const u = (TAU * (k + 0.5)) / segMinor;
      const hint = v3(Math.cos(t) * Math.cos(u), Math.sin(u), Math.sin(t) * Math.cos(u));
      emitQuad(m, P(i, k), P(i, k + 1), P(i + 1, k + 1), P(i + 1, k), { hint, smooth: 15 });
    }
  return featurize(m);
}

// ─── KIT v2 primitives ────────────────────────────────────────────────────

/** Subdivide the planar quad a-b-c-d into nu × nv cells (u along a→b, v along a→d). */
function gridQuad(m: Mesh, a: Vec3, b: Vec3, c: Vec3, d: Vec3, n: Vec3, nu: number, nv: number): void {
  const at = (u: number, v: number) => {
    const p = vAdd(vMul(a, (1 - u) * (1 - v)), vMul(b, u * (1 - v)));
    return vAdd(p, vAdd(vMul(c, u * v), vMul(d, (1 - u) * v)));
  };
  for (let i = 0; i < nu; i++)
    for (let j = 0; j < nv; j++)
      emitQuad(m, at(i / nu, j / nv), at((i + 1) / nu, j / nv), at((i + 1) / nu, (j + 1) / nv), at(i / nu, (j + 1) / nv), {
        n,
      });
}

/**
 * A cuboid centred in X/Z on y = 0 with every edge chamfered at 45° by `b`
 * (clamped to 0.45 · the smallest side): 6 face quads, 12 chamfer strips and
 * 8 corner triangles, both boundaries of every chamfer featured — the chamfer
 * catches the key light as a bright line, which is what makes painted metal
 * stop reading as cardboard. `bottom: false` (the default) omits the floor face
 * and lower chamfers for floor-standing parts. `grid` splits the faces into
 * cells no larger than that, for painter's-sort hygiene on big panels.
 * Tris: 30 open, 44 closed, +2 per extra grid cell.
 */
export function bevelBox(w: number, h: number, d: number, b: number, o: { bottom?: boolean; grid?: number } = {}): Mesh {
  const m = emptyMesh();
  const closed = !!o.bottom;
  b = Math.max(0, Math.min(b, 0.45 * Math.min(w, h, d)));
  if (b < 1e-4) {
    const bx = box(w, h, d);
    return bx;
  }
  const hx = w / 2;
  const hz = d / 2;
  const yIn = (sy: number) => (sy > 0 ? h - b : closed ? b : 0);
  const yOut = (sy: number) => (sy > 0 ? h : 0);
  const Px = (sx: number, sy: number, sz: number) => v3(sx * hx, yIn(sy), sz * (hz - b));
  const Py = (sx: number, sy: number, sz: number) => v3(sx * (hx - b), yOut(sy), sz * (hz - b));
  const Pz = (sx: number, sy: number, sz: number) => v3(sx * (hx - b), yIn(sy), sz * hz);
  const cells = (len: number) => (o.grid && o.grid > 0 ? Math.max(1, Math.ceil(len / o.grid)) : 1);
  const nx = cells(w - 2 * b);
  const nyc = cells(h - b - (closed ? b : 0));
  const nz = cells(d - 2 * b);
  const S = [-1, 1];
  const r2 = Math.SQRT1_2;
  const r3 = 1 / Math.sqrt(3);
  for (const s of S) {
    // X faces (u along z, v along y).
    gridQuad(m, Px(s, -1, -1), Px(s, -1, 1), Px(s, 1, 1), Px(s, 1, -1), v3(s, 0, 0), nz, nyc);
    // Z faces (u along x, v along y).
    gridQuad(m, Pz(-1, -1, s), Pz(1, -1, s), Pz(1, 1, s), Pz(-1, 1, s), v3(0, 0, s), nx, nyc);
    // Y faces (top always; bottom only when closed).
    if (s > 0 || closed) gridQuad(m, Py(-1, s, -1), Py(1, s, -1), Py(1, s, 1), Py(-1, s, 1), v3(0, s, 0), nx, nz);
  }
  for (const sx of S)
    for (const sy of S) {
      if (sy < 0 && !closed) continue;
      // X–Y chamfer runs along z.
      gridQuad(m, Px(sx, sy, -1), Px(sx, sy, 1), Py(sx, sy, 1), Py(sx, sy, -1), v3(sx * r2, sy * r2, 0), nz, 1);
    }
  for (const sy of S)
    for (const sz of S) {
      if (sy < 0 && !closed) continue;
      // Y–Z chamfer runs along x.
      gridQuad(m, Py(-1, sy, sz), Py(1, sy, sz), Pz(1, sy, sz), Pz(-1, sy, sz), v3(0, sy * r2, sz * r2), nx, 1);
    }
  for (const sx of S)
    for (const sz of S)
      // Vertical X–Z chamfer runs along y.
      gridQuad(m, Px(sx, -1, sz), Pz(sx, -1, sz), Pz(sx, 1, sz), Px(sx, 1, sz), v3(sx * r2, 0, sz * r2), 1, nyc);
  for (const sx of S)
    for (const sy of S)
      for (const sz of S) {
        if (sy < 0 && !closed) continue;
        emitTri(m, Px(sx, sy, sz), Py(sx, sy, sz), Pz(sx, sy, sz), { n: v3(sx * r3, sy * r3, sz * r3) });
      }
  return featurize(m, { creaseDeg: 30 });
}

/**
 * A rounded-rectangle plan (4 corners × `seg` facets, radius r) extruded in Y,
 * with the top edge rounded (`'round'`, `seg` rows), chamfered (`'bevel'`) or
 * square (`'flat'`). The cast-housing and sheet-metal primitive: gearboxes,
 * pump casings, terminal boxes, HMI housings. The start and end rows of the
 * rounding are feature lines, the rows and corners inside it are smooth.
 * Tris (open bottom, round top): s=1 32, s=2 68, s=3 116.
 */
export function roundBox(
  w: number,
  h: number,
  d: number,
  r: number,
  o: { seg?: 1 | 2 | 3; top?: 'round' | 'bevel' | 'flat'; bottom?: boolean } = {},
): Mesh {
  const m = emptyMesh();
  const s = o.seg ?? 2;
  const top = o.top ?? 'round';
  const hx = w / 2;
  const hz = d / 2;
  r = Math.max(0, Math.min(r, 0.49 * Math.min(w, d)));
  const rt = top === 'flat' ? 0 : Math.min(r, h * 0.9);
  const rows = rt <= 1e-6 ? 0 : top === 'round' ? s : 1;
  const centres: [number, number, number][] = [
    [hx - r, hz - r, 0],
    [-(hx - r), hz - r, Math.PI / 2],
    [-(hx - r), -(hz - r), Math.PI],
    [hx - r, -(hz - r), (3 * Math.PI) / 2],
  ];
  const outline = (e: number, y: number): Vec3[] => {
    const pts: Vec3[] = [];
    const rc = Math.max(0, r - e);
    for (const [cx, cz, t0] of centres)
      for (let j = 0; j <= s; j++) {
        const t = t0 + ((Math.PI / 2) * j) / s;
        pts.push(v3(cx + rc * Math.cos(t), y, cz + rc * Math.sin(t)));
      }
    return pts;
  };
  // Outline edge i → i+1: is it inside a corner (smooth columns) or a straight?
  const L = 4 * (s + 1);
  const levels: { e: number; y: number; feat: boolean; ang: number }[] = [{ e: 0, y: 0, feat: false, ang: 0 }];
  levels.push({ e: 0, y: h - rt, feat: rows > 0, ang: 0 });
  for (let j = 1; j <= rows; j++) {
    const phi = ((Math.PI / 2) * j) / rows;
    levels.push({ e: rt * (1 - Math.cos(phi)), y: h - rt + rt * Math.sin(phi), feat: j === rows, ang: phi });
  }
  const rings = levels.map((lv) => outline(lv.e, lv.y));
  for (let k = 0; k < rings.length - 1; k++) {
    const A = rings[k];
    const B = rings[k + 1];
    const phiMid = (levels[k].ang + levels[k + 1].ang) / 2;
    for (let i = 0; i < L; i++) {
      const j = (i + 1) % L;
      const mid = vMul(vAdd(A[i], A[j]), 0.5);
      const rad = vNorm(v3(mid.x, 0, mid.z));
      // Plan outward normal of this outline edge.
      const t = vSub(A[j], A[i]);
      const pn = Math.hypot(t.x, t.z) > 1e-9 ? vNorm(v3(t.z, 0, -t.x)) : rad;
      const po = vDot(pn, rad) < 0 ? vMul(pn, -1) : pn;
      const hint = v3(po.x * Math.cos(phiMid), Math.sin(phiMid), po.z * Math.cos(phiMid));
      emitQuad(m, A[i], A[j], B[j], B[i], {
        hint,
        edges: (levels[k].feat ? 1 : 0) | (levels[k + 1].feat ? 4 : 0),
        smooth: 2 | 8 | (k > 1 && !levels[k].feat ? 1 : 0) | (k + 1 > 1 && !levels[k + 1].feat && k + 1 < rings.length - 1 ? 4 : 0),
      });
    }
  }
  const capAt = (ring: Vec3[], y: number, up: boolean) => {
    const c = v3(0, y, 0);
    for (let i = 0; i < L; i++) {
      const a = ring[i];
      const b2 = ring[(i + 1) % L];
      if (dist(a, b2) < 1e-7) continue;
      emitTri(m, c, a, b2, { n: v3(0, up ? 1 : -1, 0) });
    }
  };
  capAt(rings[rings.length - 1], h, true);
  if (o.bottom) capAt(rings[0], 0, false);
  return featurize(m);
}

/** A flat annulus in y = 0 facing +Y (or −Y with `down`); both rims featured. Tris 2·seg. */
export function ring(rIn: number, rOut: number, o: { seg?: number; down?: boolean } = {}): Mesh {
  const m = emptyMesh();
  const seg = o.seg ?? segForLod(rOut, 'detail');
  const n = v3(0, o.down ? -1 : 1, 0);
  const at = (r: number, i: number) => v3(Math.cos((TAU * i) / seg) * r, 0, Math.sin((TAU * i) / seg) * r);
  for (let i = 0; i < seg; i++) {
    if (rIn > 1e-6) emitQuad(m, at(rIn, i), at(rOut, i), at(rOut, i + 1), at(rIn, i + 1), { n });
    else emitTri(m, v3(), at(rOut, i), at(rOut, i + 1), { n });
  }
  return featurize(m);
}

/** A hexagonal bolt head or nut (across-flats `af`, height h), no bottom. 16 tris. */
export function hexPrism(af: number, h: number): Mesh {
  return extrude(ngon(af / Math.sqrt(3), 6, Math.PI / 6), h, { caps: 'top' });
}

/**
 * A true screw flight along +X centred on 0, from `rIn` to `rOut`: both faces
 * (a flight is seen from both sides) plus the outer-rim strip, which is the
 * feature line. For VISIBLE lengths only — a feed opening, an open trough, a
 * crammer. Tris 6 · steps · turns (≈ 96 per turn at the default 16 steps).
 */
export function helicoid(
  rIn: number,
  rOut: number,
  len: number,
  turns: number,
  o: { steps?: number; thick?: number } = {},
): Mesh {
  const m = emptyMesh();
  const N = Math.max(4, Math.round((o.steps ?? 16) * turns));
  const th = Math.min(o.thick ?? 0.6, (len / Math.max(turns, 0.1)) * 0.3);
  const P = (i: number, r: number, dx: number) => {
    const t = (TAU * turns * i) / N;
    return v3(-len / 2 + (len * i) / N + dx, Math.cos(t) * r, Math.sin(t) * r);
  };
  for (let i = 0; i < N; i++) {
    for (const [dx, sx] of [[-th / 2, -1], [th / 2, 1]] as [number, number][])
      emitQuad(m, P(i, rIn, dx), P(i, rOut, dx), P(i + 1, rOut, dx), P(i + 1, rIn, dx), {
        hint: v3(sx, 0, 0),
        smooth: 1 | 4,
        edges: 2,
      });
    const t = (TAU * turns * (i + 0.5)) / N;
    emitQuad(m, P(i, rOut, -th / 2), P(i + 1, rOut, -th / 2), P(i + 1, rOut, th / 2), P(i, rOut, th / 2), {
      hint: v3(0, Math.cos(t), Math.sin(t)),
      smooth: 2 | 8,
      edges: 1 | 4,
    });
  }
  return featurize(m, { creaseDeg: 60 });
}

/**
 * A race-track loop in the XY plane centred on the origin (x ∈ ±len/2,
 * y ∈ ±r), width along Z: two straights and two semicircles of `seg` facets.
 * Solid by default (outer band + two ear-clipped sides, 8·seg + 4 tris);
 * `inner` makes it a hollow belt of thickness 0.18·r (16·seg + 16 tris).
 */
export function stadium(len: number, r: number, width: number, o: { seg?: number; inner?: boolean } = {}): Mesh {
  const m = emptyMesh();
  const n = o.seg ?? 8;
  const cx = Math.max(0, len / 2 - r);
  const outline = (rr: number): Vec2[] => {
    const pts: Vec2[] = [];
    for (let j = 0; j <= n; j++) {
      const t = -Math.PI / 2 + (Math.PI * j) / n;
      pts.push({ x: cx + Math.cos(t) * rr, y: Math.sin(t) * rr });
    }
    for (let j = 0; j <= n; j++) {
      const t = Math.PI / 2 + (Math.PI * j) / n;
      pts.push({ x: -cx + Math.cos(t) * rr, y: Math.sin(t) * rr });
    }
    return pts;
  };
  const hz = width / 2;
  const band = (pts: Vec2[], outward: boolean) => {
    const L = pts.length;
    for (let i = 0; i < L; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % L];
      const on = vNorm(v3(b.y - a.y, -(b.x - a.x), 0));
      emitQuad(m, v3(a.x, a.y, -hz), v3(b.x, b.y, -hz), v3(b.x, b.y, hz), v3(a.x, a.y, hz), {
        hint: outward ? on : vMul(on, -1),
        smooth: 2 | 8,
      });
    }
  };
  const outer = outline(r);
  band(outer, true);
  if (o.inner) {
    const inner = outline(r * 0.82);
    band(inner, false);
    const L = outer.length;
    for (const z of [-hz, hz])
      for (let i = 0; i < L; i++) {
        const j = (i + 1) % L;
        emitQuad(m, v3(outer[i].x, outer[i].y, z), v3(outer[j].x, outer[j].y, z), v3(inner[j].x, inner[j].y, z), v3(inner[i].x, inner[i].y, z), {
          n: v3(0, 0, Math.sign(z)),
        });
      }
  } else {
    const T = triangulate(outer);
    for (const z of [-hz, hz])
      for (let t = 0; t < T.length; t += 3) {
        const A = outer[T[t]];
        const B = outer[T[t + 1]];
        const C = outer[T[t + 2]];
        emitTri(m, v3(A.x, A.y, z), v3(B.x, B.y, z), v3(C.x, C.y, z), { n: v3(0, 0, Math.sign(z)) });
      }
  }
  return featurize(m);
}

/**
 * The closest point on a mesh's surface to `p`, and how far away it is.
 *
 * Point-to-TRIANGLE, not point-to-vertex: a coarse cylinder or a plain box has
 * vertices only at its rings and corners, so a vertex query reports a point
 * sitting flat on a face as being half a diameter away. That distinction is the
 * difference between a useful measurement and a misleading one.
 */
export function closestOnMesh(m: Mesh, p: Vec3): { q: Vec3; d: number } {
  let best = Infinity;
  let bq = p;
  const P = m.pos;
  const I = m.idx;
  for (let i = 0; i < I.length; i += 3) {
    const a = I[i] * 3;
    const b = I[i + 1] * 3;
    const c = I[i + 2] * 3;
    const q = closestOnTri(p, v3(P[a], P[a + 1], P[a + 2]), v3(P[b], P[b + 1], P[b + 2]), v3(P[c], P[c + 1], P[c + 2]));
    const d = Math.hypot(q.x - p.x, q.y - p.y, q.z - p.z);
    if (d < best) {
      best = d;
      bq = q;
      if (best < 1e-4) break;
    }
  }
  return { q: bq, d: best };
}

/** The closest point to `p` on triangle abc (Ericson, Real-Time Collision Detection). */
function closestOnTri(p: Vec3, a: Vec3, b: Vec3, c: Vec3): Vec3 {
  const ab = vSub(b, a);
  const ac = vSub(c, a);
  const ap = vSub(p, a);
  const d1 = vDot(ab, ap);
  const d2 = vDot(ac, ap);
  if (d1 <= 0 && d2 <= 0) return a;
  const bp = vSub(p, b);
  const d3 = vDot(ab, bp);
  const d4 = vDot(ac, bp);
  if (d3 >= 0 && d4 <= d3) return b;
  const vc = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) return vAdd(a, vMul(ab, d1 / (d1 - d3)));
  const cp = vSub(p, c);
  const d5 = vDot(ab, cp);
  const d6 = vDot(ac, cp);
  if (d6 >= 0 && d5 <= d6) return c;
  const vb = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) return vAdd(a, vMul(ac, d2 / (d2 - d6)));
  const va = d3 * d6 - d5 * d4;
  if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) return vAdd(b, vMul(vSub(c, b), (d4 - d3) / (d4 - d3 + (d5 - d6))));
  const den = 1 / (va + vb + vc);
  return vAdd(a, vAdd(vMul(ab, vb * den), vMul(ac, vc * den)));
}
